import type { Track } from '~/api/types'
import { coverArtUrl, streamUrl } from '~/lib/cover-art'
import { type DownloadMeta, type DownloadRecord, putDownload } from './db'

export type { DownloadMeta, DownloadRecord } from './db'
export { clearDownloads, deleteDownload, getDownloadedIds, listDownloads } from './db'

export function trackToDownloadMeta(track: Track): DownloadMeta {
  return {
    id: track.id,
    title: track.title,
    artist: track.artist,
    artistId: track.artistId,
    album: track.album,
    albumId: track.albumId,
    duration: track.duration,
    coverArt: track.coverArt,
  }
}

// Cover-art size to warm so album art renders on the offline Downloads page.
const OFFLINE_COVER_SIZE = 300

/** Download a track's audio (honoring maxBitRate via /stream) and store it for offline use. */
export async function fetchAndStore(meta: DownloadMeta): Promise<void> {
  const res = await fetch(streamUrl(meta.id), { credentials: 'include' })
  if (!res.ok) throw new Error(`Download failed for track ${meta.id} (HTTP ${res.status})`)
  const blob = await res.blob()
  const record: DownloadRecord = {
    trackId: meta.id,
    blob,
    mime: res.headers.get('content-type') ?? blob.type ?? 'audio/mpeg',
    bytes: blob.size,
    meta,
    createdAt: Date.now(),
  }
  await putDownload(record)

  // Warm the cover-art runtime cache (the service worker stores it CacheFirst).
  if (meta.coverArt) {
    void fetch(coverArtUrl('albums', meta.albumId, OFFLINE_COVER_SIZE), {
      credentials: 'include',
    }).catch(() => {})
  }
}

export async function requestPersistence(): Promise<void> {
  if (!navigator.storage?.persist) return
  if (await navigator.storage.persisted()) return
  await navigator.storage.persist().catch(() => {})
}

export async function getUsage(): Promise<{ usage: number; quota: number }> {
  if (!navigator.storage?.estimate) return { usage: 0, quota: 0 }
  const { usage = 0, quota = 0 } = await navigator.storage.estimate()
  return { usage, quota }
}
